// Small helpers shared by the domain/ modules (not React, no I/O). Ids in payloads may be upper- or lower-case (Swift
// `UUID(uuidString:)` accepts both), so every lookup here is keyed by the lower-case form (`idKey`) and every
// comparison goes through `sameId`, exactly as the contracts rules require (WEB_PLAN.md §0).

import { idKey, sameId } from '@/contracts/uuid';
import { parseWireDate } from '@/contracts/wireDate';

export { idKey, sameId };

/** Map from lower-case id to item; the first item with an id wins (Swift `first { $0.id == id }`). */
export function byId<T extends { id: string }>(items: readonly T[]): Map<string, T> {
  const map = new Map<string, T>();
  for (const item of items) {
    const key = idKey(item.id);
    if (!map.has(key)) map.set(key, item);
  }
  return map;
}

/** Looks an optional id up in a `byId` map. */
export function lookup<T>(map: ReadonlyMap<string, T>, id: string | null | undefined): T | undefined {
  return id ? map.get(idKey(id)) : undefined;
}

/** Lower-case id set. */
export function idSet(ids: Iterable<string | null | undefined>): Set<string> {
  const set = new Set<string>();
  for (const id of ids) if (id) set.add(idKey(id));
  return set;
}

/** True when `id` (any case) is in a lower-case id set. */
export function hasId(set: ReadonlySet<string>, id: string | null | undefined): boolean {
  return !!id && set.has(idKey(id));
}

/** Epoch milliseconds of a payload date; undefined when missing or unparseable. */
export function ms(value: string | null | undefined): number | undefined {
  return parseWireDate(value)?.getTime();
}

const collator = new Intl.Collator(undefined, { sensitivity: 'accent' });

/** Swift `localizedCaseInsensitiveCompare` (names in pickers, rosters, pipeline lists). */
export function compareLocalized(a: string, b: string): number {
  return collator.compare(a, b);
}

/** Swift `String <` (Unicode scalar order), used where Swift sorts by tuples of strings. */
export function compareScalars(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Swift `trimmingCharacters(in: .whitespaces)`: spaces and tabs, not newlines. */
export function trimSpaces(s: string): string {
  return s.replace(/^[\p{Zs}\t]+|[\p{Zs}\t]+$/gu, '');
}

/** Swift `trimmingCharacters(in: .whitespacesAndNewlines)`. */
export function trimAll(s: string): string {
  return s.replace(/^[\s\p{Zs}]+|[\s\p{Zs}]+$/gu, '');
}

/** `User.fullName`: "first last", trimmed. */
export function fullName(u: { firstName: string; lastName: string } | null | undefined): string {
  if (!u) return '';
  return trimSpaces(`${u.firstName} ${u.lastName}`);
}

/** `User.initials`: first letter of the first and last names, upper-cased. */
export function initials(u: { firstName: string; lastName: string }): string {
  return [u.firstName, u.lastName]
    .map((s) => Array.from(s)[0])
    .filter((c): c is string => c !== undefined)
    .join('')
    .toUpperCase();
}

/** `AppStore.userName(_:)`: the person's full name, or "Unassigned" without one. */
export function userName(users: ReadonlyMap<string, { firstName: string; lastName: string }>, id: string | null | undefined): string {
  const u = lookup(users, id);
  return u ? fullName(u) : 'Unassigned';
}
